import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation } from '@apollo/client';
import Toast from 'react-native-root-toast';

import { CREATE_MEDIA } from '../../../common/graphql/media';
import { Alert } from '../../components/other/Alert';

const isRemoteUrl = url => typeof url === 'string' && (url.startsWith('http://') || url.startsWith('https://'));

// Lead detail photos: add from the media picker (saved to the lead as they arrive) and remove.
const useLeadPhotos = ({
	id,
	photos,
	isOnline,
	captureMedia,
	capturedMediaBatch,
	processingMedia,
	modifyLead,
	refetch,
	updateLead,
}) => {
	const [isSavingPhotos, setIsSavingPhotos] = useState(false);
	const lastCapturedMediaBatchIdRef = useRef(null);
	const [createMedia] = useMutation(CREATE_MEDIA);
	const photoIds = useMemo(() => (photos || []).map(photo => photo.id), [photos]);

	const addPhotos = useCallback(() => {
		if (!isOnline) {
			Alert.alert('Internet connection required', 'Lead photos need to upload before they can be saved.');
			return;
		}

		if (!processingMedia && !isSavingPhotos) {
			captureMedia();
		}
	}, [captureMedia, isOnline, isSavingPhotos, processingMedia]);

	// Same as Sentry Clients: removing only unlinks the photo from this lead.
	const removePhoto = useCallback((photo) => {
		Alert.alert(
			'Remove this photo from the lead?',
			'',
			[
				{ text: 'Cancel', style: 'cancel' },
				{
					text: 'Remove',
					style: 'destructive',
					onPress: () => updateLead({ photos: photoIds.filter(photoId => photoId !== photo.id) }, 'Photo removed'),
				},
			],
		);
	}, [photoIds, updateLead]);

	const savePhotos = useCallback(async (links) => {
		// Offline captures come back as local files, which cannot be saved to the lead.
		const remoteLinks = links.filter(isRemoteUrl);
		const newPhotoIds = [];
		let failedCount = links.length - remoteLinks.length;

		setIsSavingPhotos(true);
		for (const link of remoteLinks) {
			try {
				const result = await createMedia({ variables: { input: { lead: id, link } } });
				const mediaId = result?.data?.createMedia?.id;
				if (!mediaId) {
					throw new Error('Photo not saved');
				}

				newPhotoIds.push(mediaId);
			}
			catch (mutationError) {
				failedCount += 1;
			}
		}

		if (newPhotoIds.length) {
			try {
				await modifyLead({ variables: { input: { id, photos: [...photoIds, ...newPhotoIds] } } });
				await refetch();
			}
			catch (mutationError) {
				failedCount = links.length;
			}
		}
		setIsSavingPhotos(false);

		if (failedCount) {
			Toast.show(failedCount === 1 ? 'A photo could not be saved' : `${failedCount} photos could not be saved`);
		}
		else {
			Toast.show(links.length === 1 ? 'Photo added' : 'Photos added');
		}
	}, [createMedia, id, modifyLead, photoIds, refetch]);

	useEffect(() => {
		if (!capturedMediaBatch || capturedMediaBatch.id === lastCapturedMediaBatchIdRef.current) {
			return;
		}

		lastCapturedMediaBatchIdRef.current = capturedMediaBatch.id;
		savePhotos(capturedMediaBatch.links || []);
	}, [capturedMediaBatch, savePhotos]);

	return { isSavingPhotos, addPhotos, removePhoto };
};

export default useLeadPhotos;
